/**
 * Phase F — Shared dialog field group.
 *
 * Renders the form body fields used by all three TableDialog variants:
 *   Table Number · Adult · Children · (optional) Comment · Location radio
 *
 * Comment is single-line (decision #1).
 * Location is bound to existing `location_type` enum (indoor/outdoor).
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function DialogFieldGroup({
  values,
  setValues,
  showComment = false,
  commentRequired = false,
  commentReadOnly = false,
  errors = {},
}) {
  const update = (key) => (e) => {
    setValues({ ...values, [key]: e.target.value });
  };

  const updateLocation = (val) => {
    setValues({ ...values, location_type: val });
  };

  return (
    <div className="px-5 py-4 space-y-3">
      <Input
        id="dlg-table-number"
        data-testid="dlg-input-table-number"
        placeholder="Table Number"
        value={values.table_number ?? ""}
        onChange={update("table_number")}
        autoComplete="off"
      />

      <div className="grid grid-cols-2 gap-3">
        <Input
          id="dlg-adult"
          data-testid="dlg-input-adult"
          placeholder="Adult"
          type="number"
          min={0}
          value={values.adult ?? ""}
          onChange={update("adult")}
        />
        <Input
          id="dlg-children"
          data-testid="dlg-input-children"
          placeholder="Children"
          type="number"
          min={0}
          value={values.children ?? ""}
          onChange={update("children")}
        />
      </div>

      {showComment && (
        <div className="space-y-1">
          <Input
            id="dlg-comment"
            data-testid="dlg-input-comment"
            placeholder={commentRequired ? "Comment *" : "Comment"}
            value={values.comments ?? ""}
            onChange={update("comments")}
            readOnly={commentReadOnly}
            autoComplete="off"
          />
          {errors.comments && (
            <p data-testid="dlg-error-comment" className="text-xs" style={{ color: "#DC2626" }}>
              {errors.comments}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-6 pt-1">
        <Label className="text-xs whitespace-nowrap" style={{ color: "#6B7280" }}>
          Location
        </Label>
        <RadioGroup
          data-testid="dlg-location-radio"
          value={values.location_type ?? ""}
          onValueChange={updateLocation}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="indoor" id="dlg-loc-indoor" data-testid="dlg-loc-indoor" />
            <Label htmlFor="dlg-loc-indoor" className="text-sm cursor-pointer">
              Indoor
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="outdoor" id="dlg-loc-outdoor" data-testid="dlg-loc-outdoor" />
            <Label htmlFor="dlg-loc-outdoor" className="text-sm cursor-pointer">
              Outdoor
            </Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

/**
 * Helper — build the form's initial state from a table object.
 * Used by Reserved/Occupied dialogs for pre-fill (decision #2).
 */
export function buildInitialValues(table) {
  return {
    table_number: table?.table_number ?? "",
    adult: table?.adult != null ? String(table.adult) : "",
    children: table?.children != null ? String(table.children) : "",
    comments: table?.comments ?? "",
    location_type: table?.location_type ?? "",
  };
}

/**
 * Convert form values to API payload.
 * Empty strings become null/undefined; numbers parsed.
 */
export function buildPayload(values, { includeComments = false } = {}) {
  const payload = {};
  if (values.table_number?.trim()) payload.table_number = values.table_number.trim();
  if (values.adult !== "" && values.adult != null) {
    const n = Number(values.adult);
    if (Number.isFinite(n)) payload.adult = n;
  }
  if (values.children !== "" && values.children != null) {
    const n = Number(values.children);
    if (Number.isFinite(n)) payload.children = n;
  }
  if (values.location_type) payload.location_type = values.location_type;
  if (includeComments && values.comments?.trim()) {
    payload.comments = values.comments.trim();
  }
  return payload;
}
